import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Horizontal card deck with edge arrows.
 *
 * Lifted verbatim out of DiscoverScreenV2, where it was a file-local helper
 * rendering the Similar Users, Trending, Group Updates and Newest Yappers rows.
 * It moved here unchanged so the Business Explore deck could use the same
 * primitive rather than growing a second one that drifts from it — and so
 * ExploreScreen does not have to import the whole DiscoverScreenV2 module to
 * get at it.
 */

// ── Horizontal scroll deck with left/right arrow buttons ─────────────────────
// Arrows are visible on sm+ (≥640px) screens and hide when at scroll limits.
// paddingTop: gives flags that overhang the card top room inside the scroll area.
export function HScrollDeck({
  children,
  paddingTop = 0,
  paddingBottom = 4,
  centerArrowsOnMedia = false,
}: {
  children: React.ReactNode;
  paddingTop?: number;
  paddingBottom?: number;
  /**
   * Centre the arrows on the first card's MEDIA rather than on the whole deck.
   *
   * The arrows sit at 50% of the deck, and a deck is its photos plus the name,
   * badge and bio lines underneath — so the midpoint lands well below the middle
   * of the photos, near their bottom edge. That is fine until something else
   * wants that corner: the Newest Yappers "Active Recently" badge moved to the
   * bottom left of the photo and collided with the left arrow.
   *
   * Opt-in, and measured rather than calculated, because only the caller's own
   * markup knows how tall its media is. Off everywhere else, so Similar Users,
   * Trending, Groups and the Business deck keep the arrows exactly where they
   * are.
   */
  centerArrowsOnMedia?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true); // start true — no right arrow until content confirms overflow
  const [mediaCentre, setMediaCentre] = useState<number | null>(null);

  const syncArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 2);
  };

  // Sync on scroll events
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', syncArrows, { passive: true });
    return () => el.removeEventListener('scroll', syncArrows);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync whenever children change (covers async data load replacing skeletons)
  useEffect(() => {
    // Small delay lets the DOM settle before measuring scrollWidth
    const t = setTimeout(syncArrows, 80);
    return () => clearTimeout(t);
  }, [children]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollDir = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstChild = el.children[0] as HTMLElement | null;
    const step = firstChild ? firstChild.offsetWidth + 12 : 240;
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
    // Re-sync after smooth scroll animation completes (~400ms)
    setTimeout(syncArrows, 420);
  };

  // Measure the first card's media box. Its first element child is the media
  // wrapper in every deck that opts in; anything else leaves mediaCentre null
  // and the arrows stay where they always were.
  useEffect(() => {
    if (!centerArrowsOnMedia) return;
    const measure = () => {
      const card = scrollRef.current?.children[0] as HTMLElement | undefined;
      const media = card?.firstElementChild as HTMLElement | undefined;
      const h = media?.getBoundingClientRect().height ?? 0;
      setMediaCentre(h > 0 ? paddingTop + h / 2 : null);
    };
    const t = setTimeout(measure, 80);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [centerArrowsOnMedia, children, paddingTop]);

  const chevronBase: React.CSSProperties = {
    position: 'absolute',
    ...(centerArrowsOnMedia && mediaCentre !== null
      ? { top: mediaCentre, transform: 'translateY(-50%)' }
      : { top: '50%', transform: 'translateY(-50%)' }),
    zIndex: 10,
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.70)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.12)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div className="relative">
      {!atStart && (
        <button
          onClick={() => scrollDir('left')}
          aria-label="Scroll left"
          style={{ ...chevronBase, left: 0 }}
        >
          <ChevronLeft className="w-4 h-4 text-white" />
        </button>
      )}
      <div
        ref={scrollRef}
        className="flex gap-3"
        style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingTop, paddingBottom }}
      >
        {children}
      </div>
      {!atEnd && (
        <button
          onClick={() => scrollDir('right')}
          aria-label="Scroll right"
          style={{ ...chevronBase, right: 0 }}
        >
          <ChevronRight className="w-4 h-4 text-white" />
        </button>
      )}
    </div>
  );
}
